import * as React from 'react'
import { AlertCircle as WarningIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  collectAdditionalProperties,
  bucketDcsParamGroups,
  useGetPlantDcsNodeDetailQuery,
  usePlantSessionPaint,
  type PlantBlockType,
  type PlantDcsCeeDetail,
  type PlantDcsControlAlarmDetail,
  type PlantDcsControlModuleDetail,
  type PlantDcsController,
  type PlantDcsControllerDetail,
  type PlantDcsControlSetpointDetail,
  type PlantDcsControlTagDetail,
  type PlantDcsFunctionBlockDetail,
  type PlantDcsIoChannelDetail,
  type PlantDcsIoModuleDetail,
  type PlantDcsNodeKind,
  type PlantDcsWiredPeer
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs'
import { Empty } from '../../components/primitives/Empty'
import { plantHref } from '../../lib/plant-links'
import { CollapsibleContent } from './CollapsibleContent'
import { EntityDetailsInspector } from './entity/EntityDetailsInspector'
import { PlantStrayPill } from './entity/PlantStrayPill'
import { PlantHref } from './PlantHref'
import { dcsIconFor } from './dcs-icon'
import {
  SessionGhostTile,
  SessionMarkAffordances,
  SessionTitleMark,
  sessionDetailHeaderClass,
  sessionMarkClass,
  useVisibleGhosts,
  type PlantSessionGhostView
} from './session-mark-ui'

const COLLAPSE_THRESHOLD = 10

export function DcsDetailPanel({ nodeId }: { nodeId: string }): React.ReactElement {
  const detail = useGetPlantDcsNodeDetailQuery(nodeId, { skip: !nodeId })

  if (detail.isError) {
    return (
      <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
        <Empty
          title="Couldn't load this DCS node"
          hint="The node may have been deleted, or the proxy is unreachable."
          icon={WarningIcon}
        />
      </div>
    )
  }
  if (detail.isLoading || !detail.data) {
    return (
      <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
        <div className="text-sm text-ink-3">Loading DCS node…</div>
      </div>
    )
  }

  switch (detail.data.kind) {
    case 'DcsController':
      return <ControllerDetail detail={detail.data} />
    case 'Cee':
      return <CeeDetail detail={detail.data} />
    case 'ControlModule':
      return <ControlModuleDetail detail={detail.data} />
    case 'FunctionBlock':
      return <FunctionBlockDetail detail={detail.data} />
    case 'IoModule':
      return <IoModuleDetail detail={detail.data} />
    case 'IoChannel':
      return <IoChannelDetail detail={detail.data} />
    case 'ControlTag':
      return <ControlTagDetail detail={detail.data} />
    case 'ControlAlarm':
      return <ControlAlarmDetail detail={detail.data} />
    case 'ControlSetpoint':
      return <ControlSetpointDetail detail={detail.data} />
  }
}

function ControllerDetail({ detail }: { detail: PlantDcsControllerDetail }): React.ReactElement {
  const { controller, cees, ioModules, commsBlocks, partner, mappedComputes } = detail
  const ceeGhosts = useVisibleGhosts(controller.id, 'Cee')
  const ioGhosts = useVisibleGhosts(controller.id, 'IoModule')
  const additionalProperties = React.useMemo(
    () =>
      collectAdditionalProperties(
        'DcsController',
        controller as unknown as Record<string, unknown>
      ),
    [controller]
  )
  return (
    <>
      <DetailHeader
        kind="DcsController"
        entityId={controller.id}
        isStray={detail.isStray}
        name={controller.display_name ?? controller.name}
        subtitle={controllerSubtitle(controller)}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="CEEs" value={cees.length} />
        <Stat label="I/O modules" value={ioModules.length} />
        <Stat label="Model" value={controller.model ?? '—'} />
        <Stat label="Role" value={controller.redundancy_role ?? '—'} />
      </div>
      {partner && (
        <NodeTileGrid label="Redundant partner">
          <NodeTile
            entityId={partner.id}
            href={plantHref(partner.id, 'DcsController', partner)}
            kind="DcsController"
            title={partner.display_name ?? partner.name}
            subtitle={partner.model}
          />
        </NodeTileGrid>
      )}
      {mappedComputes.length > 0 && (
        <section>
          <SectionHeader label="Mapped compute" />
          <div className="flex flex-wrap gap-2">
            {mappedComputes.map((compute) => (
              <PlantHref
                key={compute.id}
                id={compute.id}
                label="Compute"
                node={compute}
                className="rounded-md border bg-card px-2 py-1 text-sm font-medium text-ink no-underline shadow-sm hover:border-brand/60"
              >
                {compute.name}
              </PlantHref>
            ))}
          </div>
        </section>
      )}
      <NodeListSection
        label="CEEs"
        kind="Cee"
        items={cees}
        ghosts={ceeGhosts}
        href={(cee) => plantHref(cee.id, 'Cee', cee)}
        title={(cee) => cee.display_name ?? cee.name}
      />
      <NodeListSection
        label="I/O modules"
        kind="IoModule"
        items={ioModules}
        ghosts={ioGhosts}
        href={(mod) => plantHref(mod.id, 'IoModule', mod)}
        title={(mod) => mod.name}
        subtitle={(mod) => [mod.card_type, mod.slot, mod.iolink_name].filter(Boolean).join(' · ')}
      />
      <NodeListSection
        label="Communications"
        kind="FunctionBlock"
        items={commsBlocks}
        href={(block) => plantHref(block.id, 'FunctionBlock', block)}
        title={(block) => block.name}
        subtitle={(block) =>
          [block.block_role, block.block_type, block.template_name].filter(Boolean).join(' · ')
        }
      />
      <EntityDetailsInspector entity={controller} additionalProperties={additionalProperties} />
    </>
  )
}

function CeeDetail({ detail }: { detail: PlantDcsCeeDetail }): React.ReactElement {
  const { cee, controller, modules, commsBlocks } = detail
  const moduleGhosts = useVisibleGhosts(cee.id, 'ControlModule')
  const additionalProperties = React.useMemo(
    () => collectAdditionalProperties('Cee', cee as unknown as Record<string, unknown>),
    [cee]
  )
  return (
    <>
      <DetailHeader
        kind="Cee"
        entityId={cee.id}
        isStray={detail.isStray}
        name={cee.display_name ?? cee.name}
        parent={controller ? { kind: 'DcsController', node: controller } : null}
        subtitle={[cee.ip_address, cee.comm_path].filter(Boolean).join(' · ') || undefined}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Control modules" value={modules.length} />
        <Stat label="Communications" value={commsBlocks.length} />
        <Stat label="IP" value={cee.ip_address ?? '—'} />
      </div>
      <NodeListSection
        label="Control modules"
        kind="ControlModule"
        items={modules}
        ghosts={moduleGhosts}
        href={(mod) => plantHref(mod.id, 'ControlModule', mod)}
        title={(mod) => mod.display_name ?? mod.name}
        subtitle={(mod) => [mod.module_type, mod.period].filter(Boolean).join(' · ')}
      />
      <NodeListSection
        label="Communications"
        kind="FunctionBlock"
        items={commsBlocks}
        href={(block) => plantHref(block.id, 'FunctionBlock', block)}
        title={(block) => block.name}
        subtitle={(block) =>
          [block.block_role, block.block_type, block.template_name].filter(Boolean).join(' · ')
        }
      />
      <EntityDetailsInspector entity={cee} additionalProperties={additionalProperties} />
    </>
  )
}

function ControlModuleDetail({
  detail
}: {
  detail: PlantDcsControlModuleDetail
}): React.ReactElement {
  const { module, cee, controller, functionBlocks, sequenceBlocks, tags } = detail
  const blockGhosts = useVisibleGhosts(module.id, 'FunctionBlock')
  const additionalProperties = React.useMemo(
    () =>
      collectAdditionalProperties('ControlModule', module as unknown as Record<string, unknown>),
    [module]
  )
  return (
    <>
      <DetailHeader
        kind="ControlModule"
        entityId={module.id}
        isStray={detail.isStray}
        name={module.display_name ?? module.name}
        parent={cee ? { kind: 'Cee', node: cee } : null}
        secondaryParent={controller ? { kind: 'DcsController', node: controller } : null}
        subtitle={[module.module_type, module.period].filter(Boolean).join(' · ') || undefined}
      />
      {module.description && (
        <p className="m-0 max-w-3xl text-sm text-ink-3">{module.description}</p>
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Function blocks" value={functionBlocks.length} />
        <Stat label="Sequence" value={sequenceBlocks.length} />
        <Stat label="Control tags" value={tags.length} />
        <Stat label="EE" value={module.ee_name ?? '—'} />
      </div>
      <NodeListSection
        label="Function blocks"
        kind="FunctionBlock"
        items={functionBlocks}
        ghosts={blockGhosts}
        href={(block) => plantHref(block.id, 'FunctionBlock', block)}
        title={(block) => block.name}
        subtitle={(block) => [block.block_type, block.template_name].filter(Boolean).join(' · ')}
      />
      <NodeListSection
        label="Sequence"
        kind="FunctionBlock"
        items={sequenceBlocks}
        href={(block) => plantHref(block.id, 'FunctionBlock', block)}
        title={(block) => block.name}
        subtitle={(block) =>
          [block.block_role, block.block_type, block.template_name].filter(Boolean).join(' · ')
        }
      />
      <NodeListSection
        label="Control tags"
        kind="ControlTag"
        items={tags}
        href={(tag) => plantHref(tag.id, 'ControlTag', tag)}
        title={(tag) => tag.name}
        subtitle={(tag) => [tag.param, tag.origin_block].filter(Boolean).join(' · ')}
      />
      <EntityDetailsInspector entity={module} additionalProperties={additionalProperties} />
    </>
  )
}

function FunctionBlockDetail({
  detail
}: {
  detail: PlantDcsFunctionBlockDetail
}): React.ReactElement {
  const {
    block,
    module,
    parentBlock,
    parentCee,
    parentController,
    childBlocks,
    blockType,
    derivedFrom,
    paramGroups,
    wiredTo
  } = detail
  const additionalProperties = React.useMemo(
    () => collectAdditionalProperties('FunctionBlock', block as unknown as Record<string, unknown>),
    [block]
  )
  const grouped = React.useMemo(
    () => bucketDcsParamGroups(additionalProperties, paramGroups),
    [additionalProperties, paramGroups]
  )
  const parent = parentBlock
    ? { kind: 'FunctionBlock' as const, node: parentBlock }
    : module
      ? { kind: 'ControlModule' as const, node: module }
      : parentCee
        ? { kind: 'Cee' as const, node: parentCee }
        : parentController
          ? { kind: 'DcsController' as const, node: parentController }
          : null
  return (
    <>
      <DetailHeader
        kind="FunctionBlock"
        entityId={block.id}
        isStray={detail.isStray}
        name={block.name}
        parent={parent}
        subtitle={
          [block.block_role, block.block_type, block.template_name].filter(Boolean).join(' · ') ||
          undefined
        }
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Type" value={block.block_type ?? '—'} />
        <Stat label="Role" value={block.block_role ?? 'logic'} />
        <Stat label="Kind" value={block.kind ?? '—'} />
        <Stat label="Wired to" value={wiredTo.length} />
      </div>
      {blockType && <BlockTypeCard blockType={blockType} derivedFrom={derivedFrom} />}
      <NodeListSection
        label="Contained blocks"
        kind="FunctionBlock"
        items={childBlocks}
        href={(child) => plantHref(child.id, 'FunctionBlock', child)}
        title={(child) => child.name}
        subtitle={(child) =>
          [child.block_role, child.block_type, child.template_name].filter(Boolean).join(' · ')
        }
      />
      {wiredTo.length > 0 && (
        <NodeTileGrid label="Wired to">
          {wiredTo.map((peer) => (
            <NodeTile
              key={`${peer.id}:${peer.conn_id ?? peer.from_param ?? ''}`}
              entityId={peer.id}
              href={plantHref(peer.id, peer.kind, peer)}
              kind={peer.kind}
              title={peer.name}
              subtitle={wireSubtitle(peer)}
              badge={peer.role}
            />
          ))}
        </NodeTileGrid>
      )}
      <ParamGroupTabs buckets={grouped.buckets} />
      <EntityDetailsInspector
        entity={block}
        additionalProperties={grouped.buckets.length > 0 ? grouped.remainder : additionalProperties}
      />
    </>
  )
}

function IoModuleDetail({ detail }: { detail: PlantDcsIoModuleDetail }): React.ReactElement {
  const { ioModule, controller, channels } = detail
  const channelGhosts = useVisibleGhosts(ioModule.id, 'IoChannel')
  const additionalProperties = React.useMemo(
    () => collectAdditionalProperties('IoModule', ioModule as unknown as Record<string, unknown>),
    [ioModule]
  )
  return (
    <>
      <DetailHeader
        kind="IoModule"
        entityId={ioModule.id}
        isStray={detail.isStray}
        name={ioModule.name}
        parent={controller ? { kind: 'DcsController', node: controller } : null}
        subtitle={[ioModule.card_type, ioModule.slot, ioModule.iolink_name]
          .filter(Boolean)
          .join(' · ')}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Channels" value={channels.length} />
        <Stat label="Card type" value={ioModule.card_type ?? '—'} />
        <Stat label="IOLINK" value={ioModule.iolink_name ?? '—'} />
      </div>
      <NodeListSection
        label="Channels"
        kind="IoChannel"
        items={channels}
        ghosts={channelGhosts}
        href={(ch) => plantHref(ch.id, 'IoChannel', ch)}
        title={(ch) => ch.name}
        subtitle={(ch) =>
          [ch.channel_type, ch.channel_num, ch.spare ? 'spare' : undefined]
            .filter(Boolean)
            .join(' · ')
        }
      />
      <EntityDetailsInspector entity={ioModule} additionalProperties={additionalProperties} />
    </>
  )
}

function IoChannelDetail({ detail }: { detail: PlantDcsIoChannelDetail }): React.ReactElement {
  const { channel, ioModule, wiredFrom } = detail
  const additionalProperties = React.useMemo(
    () => collectAdditionalProperties('IoChannel', channel as unknown as Record<string, unknown>),
    [channel]
  )
  return (
    <>
      <DetailHeader
        kind="IoChannel"
        entityId={channel.id}
        isStray={detail.isStray}
        name={channel.name}
        parent={ioModule ? { kind: 'IoModule', node: ioModule } : null}
        subtitle={[channel.channel_type, channel.channel_num].filter(Boolean).join(' · ')}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Type" value={channel.channel_type ?? '—'} />
        <Stat label="Channel" value={channel.channel_num ?? '—'} />
        <Stat label="Spare" value={channel.spare ? 'yes' : 'no'} />
      </div>
      <NodeListSection
        label="Wired from"
        kind="FunctionBlock"
        items={wiredFrom}
        href={(block) => plantHref(block.id, 'FunctionBlock', block)}
        title={(block) => block.name}
        subtitle={(block) => block.block_type}
      />
      <EntityDetailsInspector entity={channel} additionalProperties={additionalProperties} />
    </>
  )
}

function ControlTagDetail({ detail }: { detail: PlantDcsControlTagDetail }): React.ReactElement {
  const { tag, module, alarms, setpoint } = detail
  const additionalProperties = React.useMemo(
    () => collectAdditionalProperties('ControlTag', tag as unknown as Record<string, unknown>),
    [tag]
  )
  return (
    <>
      <DetailHeader
        kind="ControlTag"
        entityId={tag.id}
        isStray={detail.isStray}
        name={tag.name}
        parent={module ? { kind: 'ControlModule', node: module } : null}
        subtitle={[tag.param, tag.origin_block].filter(Boolean).join(' · ') || undefined}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Param" value={tag.param ?? '—'} />
        <Stat label="Alarms" value={alarms.length} />
        <Stat label="Origin block" value={tag.origin_block ?? '—'} />
      </div>
      {setpoint && (
        <NodeTileGrid label="Setpoint">
          <NodeTile
            entityId={setpoint.id}
            href={plantHref(setpoint.id, 'ControlSetpoint', setpoint)}
            kind="ControlSetpoint"
            title={setpoint.name}
            subtitle={setpoint.value}
          />
        </NodeTileGrid>
      )}
      <NodeListSection
        label="Alarms"
        kind="ControlAlarm"
        items={alarms}
        href={(alarm) => plantHref(alarm.id, 'ControlAlarm', alarm)}
        title={(alarm) => alarm.name}
        subtitle={(alarm) => [alarm.alarm_type, alarm.priority].filter(Boolean).join(' · ')}
      />
      <EntityDetailsInspector entity={tag} additionalProperties={additionalProperties} />
    </>
  )
}

function ControlAlarmDetail({
  detail
}: {
  detail: PlantDcsControlAlarmDetail
}): React.ReactElement {
  const { alarm, tag } = detail
  const additionalProperties = React.useMemo(
    () => collectAdditionalProperties('ControlAlarm', alarm as unknown as Record<string, unknown>),
    [alarm]
  )
  return (
    <>
      <DetailHeader
        kind="ControlAlarm"
        entityId={alarm.id}
        isStray={detail.isStray}
        name={alarm.name}
        parent={tag ? { kind: 'ControlTag', node: tag } : null}
        subtitle={[alarm.alarm_type, alarm.priority].filter(Boolean).join(' · ') || undefined}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Type" value={alarm.alarm_type ?? '—'} />
        <Stat label="Priority" value={alarm.priority ?? '—'} />
      </div>
      <EntityDetailsInspector entity={alarm} additionalProperties={additionalProperties} />
    </>
  )
}

function ControlSetpointDetail({
  detail
}: {
  detail: PlantDcsControlSetpointDetail
}): React.ReactElement {
  const { setpoint, tag } = detail
  const additionalProperties = React.useMemo(
    () =>
      collectAdditionalProperties(
        'ControlSetpoint',
        setpoint as unknown as Record<string, unknown>
      ),
    [setpoint]
  )
  return (
    <>
      <DetailHeader
        kind="ControlSetpoint"
        entityId={setpoint.id}
        isStray={detail.isStray}
        name={setpoint.name}
        parent={tag ? { kind: 'ControlTag', node: tag } : null}
        subtitle={setpoint.value}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Value" value={setpoint.value ?? '—'} />
      </div>
      <EntityDetailsInspector entity={setpoint} additionalProperties={additionalProperties} />
    </>
  )
}

function BlockTypeCard({
  blockType,
  derivedFrom
}: {
  blockType: PlantBlockType
  derivedFrom?: PlantBlockType | null
}): React.ReactElement {
  return (
    <section>
      <SectionHeader label="Block type" />
      <PlantHref
        id={blockType.id}
        label="BlockType"
        node={blockType}
        className="flex items-start gap-2 rounded-xl border bg-card p-3 text-card-foreground no-underline shadow-sm hover:border-brand/60"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-ink">{blockType.name}</span>
          <span className="block truncate text-xs text-ink-3">
            {[blockType.class_name, blockType.comp_type, blockType.create_type]
              .filter(Boolean)
              .join(' · ')}
          </span>
          {derivedFrom && (
            <span className="mt-1 block truncate text-xs text-ink-3">
              Derived from {derivedFrom.name}
            </span>
          )}
        </span>
      </PlantHref>
    </section>
  )
}

function wireSubtitle(peer: PlantDcsWiredPeer): string | undefined {
  const pins = [peer.from_param, peer.to_param].filter(Boolean).join(' → ')
  return pins || undefined
}

function ParamGroupTabs({
  buckets
}: {
  buckets: Array<{ name: string; properties: Array<{ key: string; value: string }> }>
}): React.ReactElement | null {
  if (buckets.length === 0) return null
  return (
    <section>
      <SectionHeader label="Parameters" />
      <Tabs defaultValue={buckets[0]?.name} className="gap-3">
        <TabsList variant="line" aria-label="Parameter groups" className="flex-wrap">
          {buckets.map((bucket) => (
            <TabsTrigger key={bucket.name} value={bucket.name}>
              {bucket.name}
            </TabsTrigger>
          ))}
        </TabsList>
        {buckets.map((bucket) => (
          <TabsContent key={bucket.name} value={bucket.name}>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 xl:grid-cols-[repeat(auto-fill,minmax(10rem,1fr))]">
              {bucket.properties.map(({ key, value }) => (
                <div key={key} className="min-w-0">
                  <div className="text-10 font-semibold uppercase tracking-[0.06em] text-ink-4">
                    {key}
                  </div>
                  <div className="mt-0.5 break-words text-12 text-ink-2">{value}</div>
                </div>
              ))}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </section>
  )
}

function controllerSubtitle(controller: PlantDcsController): string | undefined {
  const parts = [controller.model, controller.vendor, controller.ip_address].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : undefined
}

function DetailHeader({
  kind,
  entityId,
  isStray,
  name,
  parent,
  secondaryParent,
  subtitle
}: {
  kind: PlantDcsNodeKind
  entityId: string
  isStray: boolean
  name: string
  parent?: { kind: PlantDcsNodeKind; node: { id: string; name: string } } | null
  secondaryParent?: { kind: PlantDcsNodeKind; node: { id: string; name: string } } | null
  subtitle?: string
}): React.ReactElement {
  const Icon = dcsIconFor(kind)
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(entityId)
  return (
    <header className={sessionDetailHeaderClass('flex flex-col gap-2', mark)}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-ink-3">
        <span className="text-11 font-semibold uppercase tracking-[0.04em]">{kind}</span>
        {parent && (
          <>
            <span aria-hidden>·</span>
            <ParentLink parent={parent} />
          </>
        )}
        {secondaryParent && (
          <>
            <span aria-hidden>·</span>
            <ParentLink parent={secondaryParent} />
          </>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-md bg-brand-soft text-brand">
          <Icon size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="m-0 truncate text-2xl font-semibold text-ink">{name}</h1>
            <Badge variant="outline">{kind}</Badge>
            <SessionTitleMark mark={mark} />
            <PlantStrayPill isStray={isStray} />
          </div>
          {subtitle && <p className="m-0 mt-0.5 text-sm text-ink-3">{subtitle}</p>}
        </div>
      </div>
    </header>
  )
}

function ParentLink({
  parent
}: {
  parent: { kind: PlantDcsNodeKind; node: { id: string; name: string } }
}): React.ReactElement {
  return (
    <Link
      to={plantHref(parent.node.id, parent.kind, parent.node)}
      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium text-ink-2 no-underline hover:bg-accent hover:text-accent-foreground"
    >
      <span aria-hidden>↑</span>
      <span>{parent.node.name}</span>
    </Link>
  )
}

function Stat({ label, value }: { label: string; value: number | string }): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-3 text-card-foreground shadow-sm">
      <div className="text-11 font-semibold uppercase tracking-[0.04em] text-ink-3">{label}</div>
      <div className="mt-1 text-lg font-semibold text-ink">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  )
}

function NodeTileGrid({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}): React.ReactElement {
  return (
    <section>
      <SectionHeader label={label} />
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">{children}</div>
    </section>
  )
}

function NodeTile({
  entityId,
  href,
  kind,
  title,
  subtitle,
  badge
}: {
  entityId: string
  href: string
  kind: PlantDcsNodeKind
  title: string
  subtitle?: string
  badge?: string
}): React.ReactElement {
  const Icon = dcsIconFor(kind)
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(entityId)
  return (
    <Link
      to={href}
      className={sessionMarkClass(
        'flex items-start gap-2 rounded-xl border bg-card p-3 text-card-foreground no-underline shadow-sm hover:border-brand/60',
        mark
      )}
    >
      <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-md bg-brand-soft text-brand">
        <Icon size={15} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="block min-w-0 flex-1 truncate text-sm font-medium text-ink">
            {title}
          </span>
          {badge && (
            <Badge variant="outline" className="shrink-0 text-10 font-normal">
              {badge}
            </Badge>
          )}
          <SessionMarkAffordances mark={mark} />
        </span>
        {subtitle && <span className="block truncate text-xs text-ink-3">{subtitle}</span>}
      </span>
    </Link>
  )
}

function DcsGhostTile({
  kind,
  ghost
}: {
  kind: PlantDcsNodeKind
  ghost: PlantSessionGhostView
}): React.ReactElement {
  const Icon = dcsIconFor(kind)
  return (
    <SessionGhostTile
      name={ghost.name ?? ghost.id}
      view={ghost.view}
      icon={
        <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-md bg-st-down-bg text-st-down">
          <Icon size={15} />
        </span>
      }
    />
  )
}

function NodeListSection<T extends { id: string }>({
  label,
  kind,
  items,
  ghosts = [],
  href,
  title,
  subtitle
}: {
  label: string
  kind: PlantDcsNodeKind
  items: T[]
  ghosts?: PlantSessionGhostView[]
  href: (item: T) => string
  title: (item: T) => string
  subtitle?: (item: T) => string | undefined
}): React.ReactElement | null {
  const count = items.length + ghosts.length
  if (count === 0) return null
  const tiles = (
    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
      {items.map((item) => (
        <NodeTile
          key={item.id}
          entityId={item.id}
          href={href(item)}
          kind={kind}
          title={title(item)}
          subtitle={subtitle?.(item)}
        />
      ))}
      {ghosts.map((ghost) => (
        <DcsGhostTile key={ghost.id} kind={kind} ghost={ghost} />
      ))}
    </div>
  )
  if (count <= COLLAPSE_THRESHOLD) {
    return (
      <section>
        <SectionHeader label={label} subtitle={`${count}`} />
        {tiles}
      </section>
    )
  }
  return (
    <CollapsibleRowSection label={label} count={count}>
      {tiles}
    </CollapsibleRowSection>
  )
}

function SectionHeader({
  label,
  subtitle
}: {
  label: string
  subtitle?: string
}): React.ReactElement {
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <h2 className="m-0 text-sm font-semibold text-ink">{label}</h2>
      {subtitle && <span className="text-xs text-ink-3">{subtitle}</span>}
    </div>
  )
}

function CollapsibleRowSection({
  label,
  count,
  children
}: {
  label: string
  count: number
  children: React.ReactNode
}): React.ReactElement {
  const [expanded, setExpanded] = React.useState(count <= COLLAPSE_THRESHOLD)
  return (
    <section>
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <h2 className="m-0 text-sm font-semibold text-ink">{label}</h2>
        <span className="text-xs text-ink-3">{count}</span>
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className="ml-auto m-0 border-0 bg-transparent p-0 text-12 font-medium text-brand hover:underline"
        >
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      </div>
      <CollapsibleContent open={expanded}>{children}</CollapsibleContent>
    </section>
  )
}
